import { cn } from '@/shared/lib/cn';
import { KpiGrid, type KpiItem } from '@/shared/ui/core/KpiCard';
import { ensureUniqueKpiTones, orderKpisWithTotalFirst } from '@/shared/ui/core/statusTone';
import {
  StatusDistributionChart,
  type DistributionSegment,
} from '@/shared/ui/core/StatusDistributionChart';

export type ModuleInsightsModel = {
  kpis: readonly KpiItem[];
  distribution: {
    title: string;
    segments: readonly DistributionSegment[];
  };
};

export type ModuleInsightsProps = {
  kpis: readonly KpiItem[];
  distribution?: {
    title: string;
    segments: readonly DistributionSegment[];
  };
  hideDistribution?: boolean;
  showDistribution?: boolean;
  label?: string;
  /** Shorter cards in a wrapping row, for strips with many buckets. */
  dense?: boolean;
  /** Default card height, fixed width so many buckets do not stretch the row. */
  compact?: boolean;
  variant?: 'soft' | 'solid';
  loading?: boolean;
};

/** KPI strip + status chart for module list pages (not a new BI screen). */
export function ModuleInsights({
  kpis,
  distribution,
  hideDistribution = true,
  showDistribution = false,
  label = 'Tóm tắt phân hệ',
  dense = false,
  compact = false,
  variant = 'solid',
  loading = false,
}: ModuleInsightsProps) {
  const shouldShowChart = showDistribution && !hideDistribution && Boolean(distribution);
  const orderedKpis = ensureUniqueKpiTones(kpis);

  if (loading && orderedKpis.length === 0) {
    return (
      <section className="space-y-3" aria-label={label} aria-busy>
        <div className="grid grid-cols-2 gap-3 sm:flex sm:items-stretch">
          {[1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className="flex-1 min-w-0 flex flex-col rounded-control border border-border/70 bg-surface p-3 shadow-xs animate-pulse space-y-2"
            >
              <div className="h-3.5 w-16 bg-border/60 rounded-xs" />
              <div className="h-7 w-12 bg-border/80 rounded-xs" />
            </div>
          ))}
        </div>
      </section>
    );
  }

  return (
    <section
      className={cn(
        'space-y-3 transition-opacity duration-200',
        loading && 'opacity-75',
      )}
      aria-label={label}
    >
      <KpiGrid items={orderedKpis} dense={dense} compact={compact} variant={variant} />
      {shouldShowChart && distribution ? (
        <StatusDistributionChart
          title={distribution.title}
          segments={distribution.segments}
        />
      ) : null}
    </section>
  );
}

